import { Injectable } from '@angular/core';
import type { OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
// import * as logRocket from 'logrocket';

import { DateService } from './date.service';
import { SettingsService } from '../lib/settings.service';
import type {
    TAccountProfile, TAccountStatus, TAddress, TApp, TCustomer, TDealership, TDomainInfo, TLocation,
    TSession, TSetting, TUser
} from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class SessionService implements OnDestroy {
    private settingsSubscription!: Subscription;
    private setting: TSetting = {} as TSetting;
    private isDesktopDevice = false;
    private isMobileDevice = false;
    private domainInfo!: TDomainInfo;
    private vm: any;
    private user!: TUser;
    private dealership!: TDealership;
    private accountProfile!: TAccountProfile;
    private appName: TApp;

    constructor(private settingsService: SettingsService, private dateService: DateService) {
    }

    // Cleanup when service is destroyed
    public ngOnDestroy(): void {
        this.settingsSubscription?.unsubscribe();
    }

    // ============================================================================
    // Account Profile Management
    // ============================================================================

    public setAccountProfile(profile: TAccountProfile): void {
        this.accountProfile = profile;
    }

    public getAccountProfile(): TAccountProfile {
        return this.accountProfile;
    }

    public getAccountNumber(): string {
        return this.accountProfile?.accountNumber;
    }

    public setAccountNumber(accountNumber: string): void {
        this.accountProfile.accountNumber = accountNumber;
    }

    public getAccountStatus(): TAccountStatus {
        return this.accountProfile.status;
    }

    public isAccountCancelled(): boolean {
        return (this.accountProfile.status === 'Cancelled By Customer');
    }

    public isAccountActive(): boolean {
        return ((this.accountProfile.status === 'Active') ||
            (this.accountProfile.status === 'In Trial Period'));
    }

    public getExpirationDate(): string {
        return this.accountProfile.periodInfo.expirationDate.toString();
    }

    public getIsCustomer(): boolean {
        const packageName = this.accountProfile.packageName;
        return (packageName !== 'Trial Package');
    }

    public isDebugMode(): boolean {
        return (this.accountProfile?.debug || false);
    }

    public isShowSetUserButton(): boolean {
        return (this.accountProfile?.showSetUserButton || false);
    }

    // ============================================================================
    // Dealership Management
    // ============================================================================

    public setDealership(dealership: TDealership): void {
        this.dealership = dealership;
    }

    public getDealership(): TDealership {
        return this.dealership;
    }

    // public getDealershipName(): string {
    //     return this.dealership.dealershipName;
    // }

    public getDealershipLocation(): TLocation {
        const address: TAddress = {
            'streetAddress': this.dealership.address.streetAddress,
            'city': this.dealership.address.city,
            'regionName': this.dealership.address.regionName,
            'zipcode': this.dealership.address.zipcode,
            'fullAddress': this.dealership.address.fullAddress,
            'countryName': this.dealership.address.countryName,
            'isoCountryCode': this.dealership.address.isoCountryCode,
            'coordinate': this.dealership.address.coordinate,
            'regionCode': this.dealership.address.regionCode,
            'countryAbbr': this.dealership.address.countryAbbr
        };

        const dealershipLocation: TLocation = {
            'locationName': this.dealership.dealershipName,
            'address': address
        };

        return dealershipLocation;
    }

    // ============================================================================
    // User Management
    // ============================================================================

    public setCurrentUser(user: TUser): void {
        this.user = user;
    }

    public getCurrentUser(): TUser {
        return this.user;
    }

    public get getUsersFullName(): string {
        let fullName = '';
        if (this.user) {
            fullName = this.user.firstName + ' ' + this.user.lastName;
        }
        return fullName;
    }

    public getUsersUid(): string {
        return this.user.uid;
    }

    public getIsLoggedIn(): boolean {
        return (this.accountProfile !== null) && (this.user !== null);
    }

    public getSessionData(): TSession {
        const session = {
            'email': this.user.email,
            'role': this.user.role,
            'firstName': this.user.firstName,
            'lastName': this.user.lastName,
            'userUid': this.user.uid
        };

        return session;
    }

    // ============================================================================
    // App Management
    // ============================================================================

    public setApp(appName: TApp): void {
        this.appName = appName;
    }

    public getApp(): TApp {
        return this.appName;
    }

    // ============================================================================
    // Device Management
    // ============================================================================

    public setIsDesktopDevice(isDesktop: boolean): void {
        this.isDesktopDevice = isDesktop;
    }

    public getIsDesktopDevice(): boolean {
        return this.isDesktopDevice;
    }

    public setIsMobileDevice(isMobile: boolean): void {
        this.isMobileDevice = isMobile;
    }

    public getIsMobileDevice(): boolean {
        return this.isMobileDevice;
    }

    // ============================================================================
    // Domain Management
    // ============================================================================

    public setDomainInfo(domainInfo: TDomainInfo): void {
        this.domainInfo = domainInfo;
    }

    public getDomain(): string {
        return this.domainInfo.domain;
    }

    public getDomainWithPort(): string {
        return this.domainInfo.domainWithPort;
    }

    // ============================================================================
    // Utility Methods
    // ============================================================================

    public getFakeCustomer(): TCustomer {
        const now = this.dateService.now();
        const fakeCustomer: TCustomer = {
            'customerName': 'John Doe',
            'cellPhone': this.user?.cellPhone,
            'shuttleDates': [now],
            'uid': 'fakeCustomer'
        };
        const ts = this.dateService.getMinimumDate();
        fakeCustomer.shuttleDates = [];
        fakeCustomer.shuttleDates[0] = ts;

        return fakeCustomer;
    }

    // Angular doesn't have a way to pass complex objects on the router.
    // So I will use this SessionService as a way to hold the objects that I need to pass around.
    public setViewModel(vm: any): void {
        this.vm = vm;
    }

    public getViewModel(): any {
        return this.vm;
    }
}
